import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { PasswordInput } from "@/components/PasswordInput";
import { LegalLinks } from "@/components/LegalLinks";

const TITLE = { password_change: "Confirm password change", password_reset: "Set a new password", email_change: "Confirm new email" };
const DONE = { password_change: "Your password has been changed. Sign in with your new password.", password_reset: "Your new password is set. Sign in with it now.",
  email_change: "Your email address has been updated. Use it to sign in." };
const URL = { password_change: "/auth/password/confirm", email_change: "/auth/email/confirm" };

export default function AccountConfirm() {
  const [q] = useSearchParams();
  const kind = q.get("kind"), token = q.get("token");
  const [state, setState] = useState(kind === "password_reset" ? "form" : "working");
  const [msg, setMsg] = useState("");
  const [f, setF] = useState({ p1: "", p2: "" });
  const ran = useRef(false);
  const finish = (p) => p.then(() => setState("done")).catch((e) => { setMsg(errMsg(e)); setState("error"); });
  useEffect(() => {
    if (ran.current || !TITLE[kind] || !token) { if (!TITLE[kind] || !token) { setMsg("This link is incomplete."); setState("error"); } return; }
    ran.current = true;
    if (URL[kind]) finish(api.post(URL[kind], { token }));
  }, [kind, token]);
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-4">
      <div className="po-card w-full max-w-md space-y-4 p-6" data-testid="account-confirm">
        <h1 className="text-xl font-semibold text-slate-900">{TITLE[kind] || "Account link"}</h1>
        {state === "working" && <p className="text-sm text-slate-500">Confirming…</p>}
        {state === "form" && <div className="space-y-3">
          <PasswordInput data-testid="reset-password-input" autoComplete="new-password" placeholder="New password (min 8 characters)" value={f.p1} onChange={(e) => setF({ ...f, p1: e.target.value })} />
          <PasswordInput data-testid="reset-password-confirm" autoComplete="new-password" placeholder="Confirm new password" value={f.p2} onChange={(e) => setF({ ...f, p2: e.target.value })} />
          {f.p2 && f.p1 !== f.p2 && <p className="text-xs text-rose-600">Passwords don't match.</p>}
          <Button className="w-full bg-[#13294B] hover:bg-[#1c3a66]" data-testid="reset-password-submit" disabled={f.p1.length < 8 || f.p1 !== f.p2}
            onClick={() => { setState("working"); finish(api.post("/auth/password/reset", { token, new_password: f.p1 })); }}>Set new password</Button>
        </div>}
        {state === "done" && <p className="flex items-start gap-2 text-sm text-emerald-700" data-testid="account-confirm-success"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />{DONE[kind]}</p>}
        {state === "error" && <p className="flex items-start gap-2 text-sm text-rose-600" data-testid="account-confirm-error"><XCircle className="mt-0.5 h-4 w-4 shrink-0" />{msg}</p>}
        {["done", "error"].includes(state) && <Link to="/login" className="text-sm font-medium text-[#13294B] underline" data-testid="account-confirm-login">Go to sign in</Link>}
        <LegalLinks testId="account-confirm-legal-links" className="border-t pt-3" />
      </div>
    </div>);
}
